/**
 * Centralized currency formatting for order, subscription, consultation, and checkout UIs.
 * Digit script follows app locale / `app_config.number_digits` via `formatDisplayDecimal`.
 *
 * Org dialing code drives the default profile:
 * - `966` / `+966` → SAR, 2 decimal places
 * - otherwise → KWD, 3 decimal places
 */
import { formatDisplayDecimal } from '../i18n/locale';
import { getOrganization, getSettings } from '../../data/domains/config/initialApi';
import { isSaudiDialingCode } from '../../data/domains/config/phoneDigits';

export type CurrencyProfile = {
  currency: string;
  decimals: number;
};

const KUWAIT_PROFILE: CurrencyProfile = { currency: 'KWD', decimals: 3 };
const SAUDI_PROFILE: CurrencyProfile = { currency: 'SAR', decimals: 2 };

function readOrgDialingCode(): string | number | null {
  const from_settings = getSettings()?.dialing_code;
  if (from_settings != null && String(from_settings).trim() !== '') {
    return from_settings;
  }
  const from_org = getOrganization()?.dialing_code;
  if (from_org != null && String(from_org).trim() !== '') {
    return from_org;
  }
  return null;
}

/** Map dialing code → display currency + fraction digits. */
export function resolveCurrencyProfile(
  dialing_code?: string | number | null,
): CurrencyProfile {
  const dc = dialing_code !== undefined ? dialing_code : readOrgDialingCode();
  if (isSaudiDialingCode(dc)) {
    return SAUDI_PROFILE;
  }
  return KUWAIT_PROFILE;
}

/** Active org currency code (SAR for Saudi dialing, else KWD). */
export function getDefaultCurrencyCode(
  dialing_code?: string | number | null,
): string {
  return resolveCurrencyProfile(dialing_code).currency;
}

/** Active org decimal places (2 for SAR, 3 for KWD). */
export function getDefaultCurrencyDecimals(
  dialing_code?: string | number | null,
): number {
  return resolveCurrencyProfile(dialing_code).decimals;
}

/** Fraction digits for a known currency code; unknown → org profile. */
export function decimalsForCurrencyCode(currency_code: string): number {
  const code = currency_code.trim().toUpperCase();
  if (code === 'SAR') {
    return SAUDI_PROFILE.decimals;
  }
  if (code === 'KWD') {
    return KUWAIT_PROFILE.decimals;
  }
  return getDefaultCurrencyDecimals();
}

/**
 * Numeric amount string for API payloads (ASCII digits, fixed fraction length).
 * Uses org dialing decimals unless `decimals` is passed.
 */
export function formatCurrencyAmountForApi(
  amount: number,
  decimals?: number,
): string {
  const places = decimals ?? getDefaultCurrencyDecimals();
  const value = Number(amount);
  if (Number.isNaN(value)) {
    return (0).toFixed(places);
  }
  return value.toFixed(places);
}

/**
 * Format amount with currency prefix for UI.
 * Defaults from org dialing (`966` → SAR/2, else KWD/3).
 * If only `currency` is passed, decimals follow that code (SAR→2, KWD→3).
 */
export function formatCurrency(
  amount: number,
  options?: { currency?: string; decimals?: number },
): string {
  const profile = resolveCurrencyProfile();
  const currency = options?.currency ?? profile.currency;
  const decimals =
    options?.decimals ??
    (options?.currency != null ? decimalsForCurrencyCode(options.currency) : profile.decimals);
  const value = Number(amount);
  const formatted = Number.isNaN(value)
    ? formatDisplayDecimal(0, decimals)
    : formatDisplayDecimal(value, decimals);
  return `${currency} ${formatted}`;
}
